/**
 * Layer 3 - verdict: what label does this group get?
 *
 * Two named forms, plus the escape hatch beneath them:
 * 1. Cut lines over a score - line positions and tier labels. A verdict may test several views
 *    against one ladder of labels (combined at 0.95 and no hand below 0.85); the group earns the
 *    worst label any test gives. A test may read the group by another statistic and give only
 *    some of the labels (earn on a phrase's minimum, lose only on its final value).
 * 2. An ordered ladder of tally conditions - read top-down, the first condition that holds sets
 *    the label.
 * In full generality a verdict is any rule over the complete list of judged notes; `rule` is
 * that escape hatch.
 *
 * The verdict labels one group. Whether the label counts toward progress is policy.
 */

import { ScorePoint, Statistic, ViewName, ViewScores, VIEWS } from './combining';
import { CutLines, bucketOf, cutLinesErrors } from './cutLines';
import { JudgedTake } from './judgedTake';
import { GradingConfigError } from './types';

export type VerdictView = ViewName | 'each-hand';

export interface CutLineTest {
  readonly view: VerdictView;
  /**
   * The statistic this test reads at the outermost combining level, in place of that level's
   * own (e.g. a phrase's final running score where the level keeps its minimum).
   */
  readonly statistic?: Statistic;
  /** Line positions for this test, ascending; one fewer than its labels. */
  readonly lines: readonly number[];
  /**
   * The labels this test can give, in the rule's order; it may skip some (e.g. only `lose` or
   * `earn`, never `hold`). Default: all of the rule's labels.
   */
  readonly labels?: readonly string[];
}

export type Comparison = '<' | '<=' | '==' | '>=' | '>';

export type TallyOperand = number | { readonly band: string } | { readonly total: true };

export type TallyCondition =
  | { readonly compare: readonly [TallyOperand, Comparison, TallyOperand] }
  | { readonly all: readonly TallyCondition[] }
  | { readonly any: readonly TallyCondition[] };

export interface TallyRung {
  readonly label: string;
  readonly when: TallyCondition;
}

export interface VerdictInput {
  /** The bounded group judged ('take' for a whole take), or null for a running score. */
  readonly group: string | null;
  readonly points: Readonly<Record<ViewName, ScorePoint | null>>;
  readonly take: JudgedTake;
}

export type VerdictRule =
  | {
      readonly kind: 'cut-lines';
      /** Labels from the lowest bucket to the highest. */
      readonly labels: readonly string[];
      readonly tests: readonly CutLineTest[];
      readonly atLine?: CutLines['atLine'];
      /** Which way is better, to pick the worst label across tests. Default `higher`. */
      readonly better?: 'higher' | 'lower';
    }
  | {
      readonly kind: 'tally-ladder';
      readonly view: ViewName;
      readonly rungs: readonly TallyRung[];
      readonly otherwise: string;
    }
  | { readonly kind: 'rule'; readonly decide: (input: VerdictInput) => string | null };

/** Scores read by another statistic, for the tests that ask for one (see `statistic`). */
export type StatisticScores = Readonly<Partial<Record<Statistic, ViewScores>>>;

export interface Verdict {
  readonly group: string | null;
  /** Null when there are zero cut lines (the number itself is the reading) or nothing to judge. */
  readonly label: string | null;
  /** The combined view's value for the group. */
  readonly value: number | null;
}

function testLabels(rule: Extract<VerdictRule, { kind: 'cut-lines' }>, test: CutLineTest) {
  return test.labels ?? rule.labels;
}

/** True when every label is one of the rule's, in the rule's order. */
function inRuleOrder(ruleLabels: readonly string[], labels: readonly string[]): boolean {
  const positions = labels.map((label) => ruleLabels.indexOf(label));
  return positions.every(
    (position, index) => position >= 0 && (index === 0 || position > positions[index - 1]),
  );
}

export function verdictErrors(rule: VerdictRule): string[] {
  if (rule.kind === 'cut-lines') {
    const testErrors = rule.tests.reduce<string[]>((errors, test, index) => {
      const labels = testLabels(rule, test);
      const problems = [
        ...cutLinesErrors({ lines: test.lines, labels: test.lines.length === 0 ? [] : labels }),
        ...(inRuleOrder(rule.labels, labels)
          ? []
          : ["a test's labels must be the rule's labels, in the rule's order"]),
      ];
      return [...errors, ...problems.map((error) => `verdict test ${index}: ${error}`)];
    }, []);
    return [
      ...(rule.tests.length === 0 ? ['a cut-line verdict needs at least one test'] : []),
      ...testErrors,
    ];
  }
  if (rule.kind === 'tally-ladder') {
    return rule.rungs.length === 0 ? ['a tally ladder needs at least one rung'] : [];
  }
  return [];
}

export function validateVerdict(rule: VerdictRule): void {
  const errors = verdictErrors(rule);
  if (errors.length > 0) {
    throw new GradingConfigError(`verdict: ${errors.join('; ')}`);
  }
}

function operandValue(operand: TallyOperand, tally: Readonly<Record<string, number>>): number {
  if (typeof operand === 'number') {
    return operand;
  }
  if ('band' in operand) {
    return tally[operand.band] ?? 0;
  }
  return Object.keys(tally).reduce((sum, band) => sum + tally[band], 0);
}

function compare(left: number, comparison: Comparison, right: number): boolean {
  switch (comparison) {
    case '<':
      return left < right;
    case '<=':
      return left <= right;
    case '==':
      return left === right;
    case '>=':
      return left >= right;
    case '>':
      return left > right;
  }
}

export function tallyHolds(
  condition: TallyCondition,
  tally: Readonly<Record<string, number>>,
): boolean {
  if ('all' in condition) {
    return condition.all.every((part) => tallyHolds(part, tally));
  }
  if ('any' in condition) {
    return condition.any.some((part) => tallyHolds(part, tally));
  }
  const [left, comparison, right] = condition.compare;
  return compare(operandValue(left, tally), comparison, operandValue(right, tally));
}

/** Reads a tally ladder top-down: the first rung whose condition holds sets the label. */
export function readTallyLadder(
  rungs: readonly TallyRung[],
  otherwise: string,
  tally: Readonly<Record<string, number>>,
): string {
  const rung = rungs.find((candidate) => tallyHolds(candidate.when, tally));
  return rung ? rung.label : otherwise;
}

function testedViews(view: VerdictView): ViewName[] {
  return view === 'each-hand' ? ['left', 'right'] : [view];
}

type GroupPoints = Readonly<Record<ViewName, ScorePoint | null>>;

type GroupPointsByStatistic = Readonly<Partial<Record<Statistic, GroupPoints>>>;

/** The statistics a verdict's tests read in place of the outermost level's own. */
export function statisticsRead(rule: VerdictRule): Statistic[] {
  if (rule.kind !== 'cut-lines') {
    return [];
  }
  return rule.tests.reduce<Statistic[]>(
    (found, test) =>
      test.statistic === undefined || found.indexOf(test.statistic) >= 0
        ? found
        : [...found, test.statistic],
    [],
  );
}

function cutLineLabel(
  rule: Extract<VerdictRule, { kind: 'cut-lines' }>,
  points: GroupPoints,
  pointsByStatistic: GroupPointsByStatistic,
): string | null {
  const ranks = rule.tests.reduce<number[]>((found, test) => {
    if (test.lines.length === 0) {
      return found;
    }
    const labels = testLabels(rule, test);
    const cut: CutLines = { lines: test.lines, labels, atLine: rule.atLine };
    const read = test.statistic === undefined ? points : pointsByStatistic[test.statistic];
    if (read === undefined) {
      throw new GradingConfigError(
        `verdict: a test reads the ${test.statistic} statistic, but no scores for it were given`,
      );
    }
    const values = testedViews(test.view)
      .map((view) => read[view])
      .filter((scored): scored is ScorePoint => scored !== null && scored.value !== null)
      .map((scored) => rule.labels.indexOf(labels[bucketOf(cut, scored.value as number)]));
    return [...found, ...values];
  }, []);
  if (ranks.length === 0) {
    return null;
  }
  const worst = (rule.better ?? 'higher') === 'higher' ? Math.min(...ranks) : Math.max(...ranks);
  return rule.labels[worst];
}

function labelFor(
  rule: VerdictRule,
  input: VerdictInput,
  pointsByStatistic: GroupPointsByStatistic,
): string | null {
  if (rule.kind === 'rule') {
    return rule.decide(input);
  }
  if (rule.kind === 'tally-ladder') {
    const scored = input.points[rule.view];
    return scored && scored.tally
      ? readTallyLadder(rule.rungs, rule.otherwise, scored.tally)
      : null;
  }
  return cutLineLabel(rule, input.points, pointsByStatistic);
}

function pointsForGroup(
  scores: ViewScores,
  group: string | null,
): Record<ViewName, ScorePoint | null> {
  return VIEWS.reduce<Record<ViewName, ScorePoint | null>>(
    (found, view) => {
      const viewScore = scores[view];
      const scored =
        group === null
          ? viewScore.latest
          : (viewScore.points.find((candidate) => candidate.group === group) ?? null);
      return { ...found, [view]: scored };
    },
    { combined: null, left: null, right: null },
  );
}

/**
 * One verdict per bounded group of the combined view (in order), or one verdict on the latest
 * running value when the outermost level is rolling. `byStatistic` holds the scores for the
 * tests that read another statistic ({@link statisticsRead}); `scoreTake` supplies them.
 */
export function judgeVerdicts(
  rule: VerdictRule,
  scores: ViewScores,
  take: JudgedTake,
  byStatistic: StatisticScores = {},
): Verdict[] {
  validateVerdict(rule);
  const combinedPoints = scores.combined.points;
  const rolling = combinedPoints.length > 0 && combinedPoints[0].group === null;
  const groups: (string | null)[] =
    rolling || combinedPoints.length === 0 ? [null] : combinedPoints.map((scored) => scored.group);
  return groups.map((group) => {
    const points = pointsForGroup(scores, group);
    const pointsByStatistic = (
      Object.keys(byStatistic) as Statistic[]
    ).reduce<GroupPointsByStatistic>(
      (found, statistic) => ({
        ...found,
        [statistic]: pointsForGroup(byStatistic[statistic] as ViewScores, group),
      }),
      {},
    );
    return {
      group,
      label: labelFor(rule, { group, points, take }, pointsByStatistic),
      value: points.combined ? points.combined.value : null,
    };
  });
}
